import * as React from 'react';

import { cn } from '../../lib/cn';

const ICON_BG = {
	accent: 'color-mix(in srgb, var(--color-accent) 15%, transparent)',
	accentSoft: 'color-mix(in srgb, var(--color-accent) 10%, transparent)',
	success: 'rgba(16,185,129,0.2)',
	successSoft: 'rgba(16,185,129,0.15)',
	warn: 'rgba(245,158,11,0.2)',
	warnSoft: 'rgba(245,158,11,0.15)',
	purple: 'color-mix(in srgb, var(--color-accent-purple) 20%, transparent)',
	danger: 'rgba(239,68,68,0.15)',
} as const;

export type IconBgTone = keyof typeof ICON_BG;

export { ICON_BG };

export interface SideGridProps {
	/** Contenuto principale e colonna laterale fissa. */
	children: React.ReactNode;
}

/** Layout a due colonne: contenuto principale + colonna laterale fissa a 300px (legacy `gside`). */
const SideGrid = ({ children }: SideGridProps) => <div className="gside">{children}</div>;
SideGrid.displayName = 'SideGrid';

export interface FlexColumnProps {
	/** Card/pannelli da impilare verticalmente. */
	children: React.ReactNode;
}

/** Colonna flex che permette alle card impilate di allungarsi (`flex: 1`). */
const FlexColumn = ({ children }: FlexColumnProps) => (
	<div style={{ display: 'flex', flexDirection: 'column' }}>{children}</div>
);
FlexColumn.displayName = 'FlexColumn';

export interface MilestoneItemProps {
	/** Icona del traguardo. */
	icon: React.ReactNode;
	/** Colore di sfondo dell'icona. Default: `success`. */
	iconTone?: IconBgTone;
	/** Titolo del traguardo raggiunto. */
	title: React.ReactNode;
	/** Testo secondario (es. data, dettaglio). */
	sub: React.ReactNode;
	/** Testo del tag a destra. */
	tag: React.ReactNode;
	/** Colore del tag. */
	tagTone: 'green' | 'orange' | 'purple';
}

/** Riga di un traguardo di gamification (legacy `milestone`). */
const MilestoneItem = ({
	icon,
	iconTone = 'success',
	title,
	sub,
	tag,
	tagTone,
}: MilestoneItemProps) => (
	<div className="milestone">
		<div className="ms-badge" style={{ background: ICON_BG[iconTone] }}>
			{icon}
		</div>
		<div>
			<div className="ms-title">{title}</div>
			<div className="ms-sub">{sub}</div>
		</div>
		<div className={cn('ms-tag', `badge-${tagTone}`)}>{tag}</div>
	</div>
);
MilestoneItem.displayName = 'MilestoneItem';

export interface TotalStripProps {
	/** Etichetta del totale. */
	label: React.ReactNode;
	/** Testo secondario sotto l'etichetta. */
	sub?: React.ReactNode;
	/** Valore totale, mostrato in grande a destra. */
	value: React.ReactNode;
}

/** Fascia riepilogo totale (colore warning) con valore grande monospaziato a destra. */
const TotalStrip = ({ label, sub, value }: TotalStripProps) => (
	<div
		style={{
			background: 'rgba(245,158,11,0.08)',
			border: '1px solid rgba(245,158,11,0.25)',
			borderRadius: 8,
			padding: '9px 12px',
			marginTop: 10,
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
		}}
	>
		<div>
			<div
				style={{
					fontSize: 11,
					color: 'var(--color-text-muted)',
					textTransform: 'uppercase',
					letterSpacing: 1,
				}}
			>
				{label}
			</div>
			{sub && (
				<div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 1 }}>{sub}</div>
			)}
		</div>
		<div
			style={{
				fontSize: 20,
				fontWeight: 800,
				fontFamily: 'var(--font-mono)',
				color: 'var(--color-warn)',
			}}
		>
			{value}
		</div>
	</div>
);
TotalStrip.displayName = 'TotalStrip';

export interface IncentiveRowProps {
	/** Icona della raccomandazione/incentivo. */
	icon: React.ReactNode;
	/** Colore di sfondo dell'icona. Default: `accentSoft`. */
	iconTone?: IconBgTone;
	/** Nome dell'incentivo/raccomandazione. */
	name: React.ReactNode;
	/** Descrizione. */
	desc: React.ReactNode;
	/** Azione opzionale allineata a destra (bottone/link). */
	action?: React.ReactNode;
}

/** Riga di raccomandazione o incentivo (legacy `inc-row`). */
const IncentiveRow = ({ icon, iconTone = 'accentSoft', name, desc, action }: IncentiveRowProps) => (
	<div className="inc-row">
		<div className="inc-icon" style={{ background: ICON_BG[iconTone] }}>
			{icon}
		</div>
		<div style={{ flex: 1 }}>
			<div className="inc-name">{name}</div>
			<div className="inc-desc">{desc}</div>
		</div>
		{action && <div style={{ marginLeft: 'auto', flexShrink: 0 }}>{action}</div>}
	</div>
);
IncentiveRow.displayName = 'IncentiveRow';

export interface AiBannerBoxProps {
	/** Icona del banner. */
	icon: React.ReactNode;
	/** Titolo dell'insight. */
	title: React.ReactNode;
	/** Testo dell'insight generato dall'AI. */
	children: React.ReactNode;
	/** Badge opzionale mostrato a destra. */
	badge?: React.ReactNode;
}

/** Banner di insight generato dall'AI (legacy `ai-banner`). */
const AiBannerBox = ({ icon, title, children, badge }: AiBannerBoxProps) => (
	<div className="ai-banner">
		<div className="ai-icon">{icon}</div>
		<div className="ai-text">
			<h2>{title}</h2>
			<p>{children}</p>
		</div>
		{badge && <div className="ai-badge">{badge}</div>}
	</div>
);
AiBannerBox.displayName = 'AiBannerBox';

export interface PctBarCellProps {
	/** Percentuale da mostrare (0-100). */
	pct: number;
	/** Se true, usa i colori "buono" (verde/accento) invece di quelli di attenzione (warning). */
	good: boolean;
}

/** Percentuale monospaziata + barra a gradiente sottile per celle di tabella (es. quota di autoconsumo). */
const PctBarCell = ({ pct, good }: PctBarCellProps) => (
	<>
		<div
			style={{
				fontFamily: 'var(--font-mono)',
				color: good ? 'var(--color-accent-green)' : 'var(--color-warn)',
				marginBottom: 2,
			}}
		>
			{pct}%
		</div>
		<div
			style={{ height: 4, background: 'var(--color-border)', borderRadius: 2, overflow: 'hidden' }}
		>
			<div
				style={{
					width: `${Math.max(0, Math.min(100, pct))}%`,
					height: '100%',
					background: good
						? 'linear-gradient(90deg,var(--color-accent-green),var(--color-accent))'
						: 'linear-gradient(90deg,var(--color-warn),rgba(245,158,11,0.4))',
					borderRadius: 2,
				}}
			/>
		</div>
	</>
);
PctBarCell.displayName = 'PctBarCell';

export interface LabeledBoxProps {
	/** Etichetta micro maiuscola sopra il contenuto. */
	label: React.ReactNode;
	/** Contenuto del box. */
	children: React.ReactNode;
}

/**
 * Box bordato con etichetta micro maiuscola (es. visualizzazione regole CER).
 *
 * Bordo/sfondo/padding/radius allineati al mock (`border` piena non /60,
 * `bg-card` non `bg-background`, `13px 15px` non `p-3`, radius 9px non 8):
 * unico uso nel repo è "Regole della comunità" di MySlims, sicuro cambiarlo
 * senza controllare altri chiamanti.
 */
const LabeledBox = ({ label, children }: LabeledBoxProps) => (
	<div className="rounded-[9px] border border-border bg-card px-[15px] py-[13px]">
		<div className="mb-[5px] text-[11px] font-extrabold tracking-[.6px] text-text-primary uppercase">
			{label}
		</div>
		{children}
	</div>
);
LabeledBox.displayName = 'LabeledBox';

export interface OverlayAreaProps {
	/** Messaggio flottante centrato mostrato sopra il contenuto (es. stato vuoto/caricamento di un grafico). */
	overlay?: React.ReactNode;
	/** Contenuto sottostante (es. il grafico). */
	children: React.ReactNode;
	/**
	 * Colonna flex che riempie il genitore invece di fermarsi al proprio
	 * contenuto — serve quando dentro c'è un grafico `fillHeight` (es.
	 * `SlChart`) sotto una `PanelCard fill`: senza, l'area resta alta quanto
	 * grafico+didascalia e lascia vuoto lo spazio residuo della card, perché
	 * di default un figlio flex non cresce (`flex: 0 1 auto`).
	 */
	fill?: boolean;
}

/** Area relativa con un messaggio flottante centrato opzionale (stati vuoto/caricamento sopra un grafico). */
const OverlayArea = ({ overlay, children, fill }: OverlayAreaProps) => (
	<div className={cn('relative', fill && 'flex min-h-0 flex-1 flex-col')}>
		{children}
		{overlay && (
			<div className="pointer-events-none absolute inset-0 flex items-center justify-center">
				<span className="rounded-md bg-card/80 px-3 py-1.5 text-[13px] text-text-muted backdrop-blur-sm">
					{overlay}
				</span>
			</div>
		)}
	</div>
);
OverlayArea.displayName = 'OverlayArea';

export interface CardGridSpan2Props {
	/** Contenuto della card larga (tipicamente un grafico). */
	children: React.ReactNode;
}

/**
 * Card che occupa due colonne dentro una CardGrid (card grafico larga).
 *
 * `h-full` non è decorativo: la cella della griglia è già alta quanto la riga,
 * ma senza un'altezza esplicita la card dentro si ferma al proprio contenuto e
 * una card `fill` non avrebbe niente su cui allungarsi.
 */
const CardGridSpan2 = ({ children }: CardGridSpan2Props) => (
	<div className="h-full" style={{ gridColumn: 'span 2' }}>
		{children}
	</div>
);
CardGridSpan2.displayName = 'CardGridSpan2';

export interface ChartSummaryRowProps {
	/** Coppie etichetta/valore da mostrare in riga. */
	children: React.ReactNode;
	/**
	 * Scala ridotta (11px) e più staccata dal grafico, per i riquadri stretti: è
	 * la fascia che il mock v11 mette sotto i profili orari da 186px, dove i 13px
	 * della versione piena manderebbero le tre coppie a capo.
	 */
	compact?: boolean;
}

/** Fascia di statistiche inline sotto un grafico (coppie etichetta/valore che vanno a capo). */
const ChartSummaryRow = ({ children, compact }: ChartSummaryRowProps) => (
	<div
		className={cn(
			'flex flex-wrap items-center',
			compact ? 'mt-5 gap-3.5 text-[11px]' : 'mt-2 gap-4 text-[13px]',
		)}
	>
		{children}
	</div>
);
ChartSummaryRow.displayName = 'ChartSummaryRow';

export {
	AiBannerBox,
	CardGridSpan2,
	ChartSummaryRow,
	FlexColumn,
	IncentiveRow,
	LabeledBox,
	MilestoneItem,
	OverlayArea,
	PctBarCell,
	SideGrid,
	TotalStrip,
};
